import clsx from 'clsx'
import { PropertyValueMap, unsafeCSS } from 'lit'
import {
  customElement,
  property,
  queryAssignedElements,
} from 'lit/decorators.js'
import { html } from 'lit/static-html.js'

import { AccordionVariant } from '@/dap-design-system'

import { DdsElement } from '../../internal/dds-hu-element'
import DapDSAccordion from '../accordion/accordion.component'
import styles from './accordion-group.scss?inline'

/**
 * `dap-ds-accordion-group` is a custom accordion group component.
 * @element dap-ds-accordion-group
 * @summary An accordion group is a collection of accordion components.
 * @title - Accordion group
 * @group accordion
 *
 * @slot - The content of the accordion group.
 *
 * @csspart base - The main accordion group container.
 *
 * @cssproperty --dds-accordion-group-spacing - Controls the gap between accordion items (default: var(--dds-spacing-300))
 * @cssproperty --dds-accordion-group-border-color - Controls the border color (default: var(--dds-border-neutral-subtle))
 * @cssproperty --dds-accordion-group-border-width - Controls the border width (default: var(--dds-border-width-base))
 * @cssproperty --dds-accordion-group-border-radius - Controls the border radius (default: var(--dds-radius-base))
 * @cssproperty --dds-accordion-group-background - Controls the background color (default: transparent)
 */
@customElement('dap-ds-accordion-group')
export default class DapDSAccordionGroup extends DdsElement {
  @queryAssignedElements({ selector: 'dap-ds-accordion' })
  private readonly accordions!: Array<DapDSAccordion>

  /** Whether to close other accordions when one is opened. */
  @property({ type: String }) autoClose = 'true'

  /** The variant of the accordion
   * @type { 'default' | 'collapsed' | 'clean' | 'clean-collapsed' }
   */
  @property({ type: String }) variant: AccordionVariant = 'default'

  static readonly styles = unsafeCSS(styles)

  private readonly boundOnAccordionChange = this.onAccordionChange.bind(this)
  private readonly boundOnSlotChange = this.onSlotChange.bind(this)

  connectedCallback(): void {
    super.connectedCallback()
    this.addEventListener('slotchange', this.boundOnSlotChange)
  }

  disconnectedCallback(): void {
    super.disconnectedCallback()
    this.removeEventListener('slotchange', this.boundOnSlotChange)
    this.accordions.forEach(accordion => {
      accordion.removeEventListener('dds-opened', this.boundOnAccordionChange)
    })
  }

  protected firstUpdated(
    _changedProperties:
      | PropertyValueMap<DapDSAccordionGroup>
      | Map<PropertyKey, unknown>,
  ): void {
    this.setupAccordions()
  }

  private onSlotChange(): void {
    this.setupAccordions()
  }

  private setupAccordions(): void {
    this.accordions.forEach((accordion, index) => {
      accordion.variant = this.variant
      accordion.lastItem = index === this.accordions.length - 1

      accordion.removeEventListener('dds-opened', this.boundOnAccordionChange)
      accordion.addEventListener('dds-opened', this.boundOnAccordionChange)
    })
  }

  private onAccordionChange(event: CustomEvent): void {
    const openedAccordion = event.detail?.item

    if (!openedAccordion) {
      return
    }

    if (this.autoClose === 'true') {
      this.accordions.forEach(accordion => {
        if (openedAccordion !== accordion) {
          accordion.removeAttribute('opened')
        }
      })
    }
  }

  render() {
    return html`
      <div
        part="base"
        class=${clsx('accordion-group', `accordion-group--${this.variant}`)}>
        <slot></slot>
      </div>
    `
  }
}
